import { useState } from 'react';
import { CalendarClock, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { useDispensingSettingsAdmin, useUpdateDispensingSettings } from '@/features/pharmacy/useNearExpiry';
import { AdjustmentApprovalCard } from './AdjustmentApprovalCard';
import { ReorderPlanningCard } from './ReorderPlanningCard';

// Mirrors utils/pharmacyExpiry.js's NEAR_EXPIRY_MIN/MAX — the server re-checks.
const MIN_DAYS = 1;
const MAX_DAYS = 730;

/**
 * Admin Panel → 💊 Pharmacy → ⏳ Dispensing & Expiry — the hospital's
 * near-expiry window (pharmacy_dispensing_settings, tenant-wide).
 *
 * The window drives three things: the "Near expiry" warning on Dispensing,
 * the Visit Workflow's Pharmacy stage and the walk-in counter; the Pharmacy →
 * Near Expiry report; and "Expiring soon" in Reports → Pharmacy. It is a
 * warning only — expired stock is always blocked, whatever this is set to.
 */
export default function DispensingSettingsPanel() {
  const query = useDispensingSettingsAdmin();
  const update = useUpdateDispensingSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = query.data;
  if (!settings) {
    return query.error
      ? <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{query.error instanceof ApiError ? query.error.message : 'Could not load the settings.'}</div>
      : <LoadingState />;
  }

  const value = draft ?? String(settings.near_expiry_days);
  const days = Number(value);
  const localError = !Number.isInteger(days) || days < MIN_DAYS || days > MAX_DAYS
    ? `Enter a whole number of days from ${MIN_DAYS} to ${MAX_DAYS}.`
    : null;
  const dirty = draft !== null && days !== settings.near_expiry_days;
  const apiError = update.error instanceof ApiError ? update.error.message : null;

  const save = () => {
    if (localError) return;
    update.mutate({ near_expiry_days: days }, { onSuccess: () => { setDraft(null); setSaved(true); } });
  };

  return (
    <div className="max-w-[900px] space-y-4 pb-24">
      {apiError && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{apiError}</div>}

      <Card>
        <CardHeader><CardTitle><CalendarClock size={15} /> Near-expiry window</CardTitle></CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-1">
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium">Flag a batch as “Near expiry” when fewer than</div>
            <div className="text-[11px] leading-snug text-ink-muted">
              Shown as a warning when the batch is picked for dispensing, and used for the Near Expiry stock report.
              Default 90 days.
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Input
              type="number"
              min={MIN_DAYS}
              max={MAX_DAYS}
              className="h-8 w-24 tabular-nums"
              value={value}
              invalid={!!localError}
              onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
            />
            <span className="text-[11.5px] text-ink-muted">days remain</span>
          </div>
        </div>
      </Card>

      <AdjustmentApprovalCard settings={settings} />
      <ReorderPlanningCard settings={settings} />

      <div className="flex gap-2.5 rounded-lg bg-info-soft px-3 py-2.5 text-[11.5px] text-info-ink">
        <Info size={15} className="mt-px shrink-0" />
        <p className="leading-relaxed">
          Dispensing always picks the earliest-expiring batch with enough stock. Choosing a later batch while an
          earlier one is available needs a reason, which is saved on the bill line. Near-expiry is a warning only;
          expired stock can never be dispensed. Every change is recorded in the audit log
          {settings.updated_by ? <> — last changed by <b>{settings.updated_by}</b>{settings.updated_at ? ` on ${new Date(settings.updated_at).toLocaleString('en-IN')}` : ''}</> : ''}.
        </p>
      </div>

      {(dirty || saved) && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
            {dirty ? (
              <>
                <span className={cn('text-[12.5px] font-medium', localError && 'text-danger-ink')}>{localError ?? 'Unsaved changes'}</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setDraft(null); setSaved(false); update.reset(); }}>Discard</Button>
                  <Button size="sm" disabled={!!localError} loading={update.isPending} onClick={save}>
                    Save near-expiry window
                  </Button>
                </div>
              </>
            ) : (
              <span className="text-[12.5px] font-medium text-success-ink">✓ Saved.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
